import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users, Tag, Star, Check, UserPlus, Clock, ArrowLeft,
  Loader2, MessageCircle, User, Heart, Send, Trash2,
  ChevronDown, ChevronUp, PenSquare, Car, Shield, ImagePlus, X, Pin
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../context/AuthContext';
import { feedAPI, followAPI, profileAPI } from '../lib/api';
import { toast } from 'sonner';
import NotificationBell from '../components/NotificationBell';
import ChatButton from '../components/ChatButton';
import useOnlineStatus from '../hooks/useOnlineStatus';
import { devError } from '../lib/devLog';

const FEED_ICONS = {
  post: PenSquare,
  task_created: Tag,
  task_completed: Check,
  bid_accepted: Check,
  rating_given: Star,
  new_driver: UserPlus,
};

const FEED_COLORS = {
  post: 'bg-orange-100 text-orange-600',
  task_created: 'bg-blue-100 text-blue-600',
  task_completed: 'bg-green-100 text-green-600',
  bid_accepted: 'bg-purple-100 text-purple-600',
  rating_given: 'bg-yellow-100 text-yellow-600',
  new_driver: 'bg-orange-100 text-orange-600',
};

const getRoleIcon = (role) => {
  if (role === 'driver') return Car;
  if (role === 'admin') return Shield;
  return User;
};

const getRoleBadge = (role) => {
  const styles = {
    driver: 'bg-orange-100 text-orange-700',
    admin: 'bg-purple-100 text-purple-700',
    user: 'bg-blue-100 text-blue-700',
  };
  return styles[role] || styles.user;
};

const CommunityFeed = ({ embedded = false }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [communityFeed, setCommunityFeed] = useState([]);
  const [followingFeed, setFollowingFeed] = useState([]);
  const [following, setFollowing] = useState([]);
  const [followers, setFollowers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newPost, setNewPost] = useState('');
  const [posting, setPosting] = useState(false);
  const [postImage, setPostImage] = useState(null);
  const [postImagePreview, setPostImagePreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [isAnnouncement, setIsAnnouncement] = useState(false);
  const [isPinned, setIsPinned] = useState(false);
  const [expandedComments, setExpandedComments] = useState({});
  const [commentTexts, setCommentTexts] = useState({});
  const [comments, setComments] = useState({});
  const [loadingComments, setLoadingComments] = useState({});

  useOnlineStatus();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [communityRes, followingFeedRes, followingRes, followersRes] = await Promise.all([
        feedAPI.getCommunityFeed(),
        feedAPI.getFollowingFeed(),
        followAPI.getFollowing(),
        followAPI.getFollowers(),
      ]);
      setCommunityFeed(communityRes.data);
      setFollowingFeed(followingFeedRes.data);
      setFollowing(followingRes.data);
      setFollowers(followersRes.data);
    } catch (error) {
      devError('Failed to fetch feed:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be under 5MB');
      return;
    }
    setPostImage(file);
    setPostImagePreview(URL.createObjectURL(file));
  };

  const removeImage = () => {
    setPostImage(null);
    if (postImagePreview) URL.revokeObjectURL(postImagePreview);
    setPostImagePreview(null);
  };

  const handleCreatePost = async () => {
    if (!newPost.trim() && !postImage) return;
    setPosting(true);
    try {
      let imageUrl = null;
      if (postImage) {
        setUploadingImage(true);
        const uploadRes = await feedAPI.uploadImage(postImage);
        imageUrl = uploadRes.data.image_url;
        setUploadingImage(false);
      }
      await feedAPI.createPost(newPost.trim(), imageUrl, {
        is_announcement: user?.role === 'admin' ? isAnnouncement : false,
        is_pinned: user?.role === 'admin' ? isPinned : false,
      });
      setNewPost('');
      removeImage();
      setIsAnnouncement(false);
      setIsPinned(false);
      toast.success('Post shared!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create post');
    } finally {
      setPosting(false);
      setUploadingImage(false);
    }
  };

  const handleLike = async (postId) => {
    try {
      await feedAPI.likePost(postId);
      fetchData();
    } catch (error) {
      toast.error('Failed to like post');
    }
  };

  const handleComment = async (postId) => {
    const text = commentTexts[postId]?.trim();
    if (!text) return;
    try {
      await feedAPI.commentOnPost(postId, text);
      setCommentTexts(prev => ({ ...prev, [postId]: '' }));
      fetchComments(postId);
      fetchData();
    } catch (error) {
      toast.error('Failed to comment');
    }
  };

  const fetchComments = async (postId) => {
    setLoadingComments(prev => ({ ...prev, [postId]: true }));
    try {
      const res = await feedAPI.getComments(postId);
      setComments(prev => ({ ...prev, [postId]: res.data }));
    } catch (error) {
      devError(error);
    } finally {
      setLoadingComments(prev => ({ ...prev, [postId]: false }));
    }
  };

  const toggleComments = (postId) => {
    const isExpanded = expandedComments[postId];
    setExpandedComments(prev => ({ ...prev, [postId]: !isExpanded }));
    if (!isExpanded && !comments[postId]) {
      fetchComments(postId);
    }
  };

  const handleDeletePost = async (postId) => {
    try {
      await feedAPI.deletePost(postId);
      toast.success('Post deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete post');
    }
  };

  const handleUnfollow = async (userId) => {
    try {
      await followAPI.unfollow(userId);
      toast.success('Unfollowed');
      fetchData();
    } catch (error) {
      toast.error('Failed to unfollow');
    }
  };

  const formatTime = (isoString) => {
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const renderFeedItem = (item) => {
    const Icon = FEED_ICONS[item.type] || Tag;
    const colorClass = FEED_COLORS[item.type] || 'bg-zinc-100 text-zinc-600';
    const isPost = item.type === 'post';
    const isLiked = (item.likes || []).includes(user?.id);
    const likesCount = (item.likes || []).length;
    const commentCount = item.comment_count || 0;
    const canDelete = item.user_id === user?.id || user?.role === 'admin';
    const RoleIcon = getRoleIcon(item.user_role);

    return (
      <div key={item.id} className="bg-white rounded-xl border border-zinc-200 overflow-hidden" data-testid={`feed-item-${item.id}`}>
        {/* Header */}
        <div className="p-4 pb-2">
          <div className="flex items-start gap-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${isPost ? 'bg-zinc-100' : colorClass}`}>
              {isPost ? <RoleIcon className="w-5 h-5 text-zinc-500" /> : <Icon className="w-5 h-5" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-sm text-zinc-900">{item.user_name}</span>
                {item.user_role && (
                  <span className={`text-xs px-1.5 py-0.5 rounded-full ${getRoleBadge(item.user_role)}`}>
                    {item.user_role}
                  </span>
                )}
              </div>
              <p className="text-xs text-zinc-400 flex items-center gap-1 mt-0.5">
                <Clock className="w-3 h-3" />
                {formatTime(item.created_at)}
              </p>
            </div>
            {canDelete && isPost && (
              <div className="flex items-center gap-1">
                {user?.role === 'admin' && (
                  <button
                    onClick={async () => { await feedAPI.pinPost(item.id); fetchData(); }}
                    className={`transition-colors ${item.is_pinned ? 'text-orange-500' : 'text-zinc-300 hover:text-orange-500'}`}
                    title={item.is_pinned ? 'Unpin' : 'Pin to top'}
                    data-testid={`pin-post-${item.id}`}
                  >
                    <Pin className="w-4 h-4" />
                  </button>
                )}
                <button
                  onClick={() => handleDeletePost(item.id)}
                  className="text-zinc-300 hover:text-red-500 transition-colors"
                  data-testid={`delete-post-${item.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="px-4 pb-3">
          {(item.is_pinned || item.is_announcement) && (
            <div className="flex items-center gap-2 mb-2">
              {item.is_pinned && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 font-medium flex items-center gap-1">
                  <Pin className="w-3 h-3" /> Pinned
                </span>
              )}
              {item.is_announcement && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-medium">Announcement</span>
              )}
            </div>
          )}
          {item.content && (
            <p className="text-sm text-zinc-800 leading-relaxed whitespace-pre-wrap">{item.content}</p>
          )}
          {item.data?.image_url && (
            <div className="mt-2">
              <img
                src={`${process.env.REACT_APP_BACKEND_URL}${item.data.image_url}`}
                alt="Post image"
                className="rounded-lg max-h-96 w-full object-cover border border-zinc-200 cursor-pointer hover:opacity-95 transition-opacity"
                onClick={(e) => { window.open(e.target.src, '_blank'); }}
                data-testid={`post-image-${item.id}`}
              />
            </div>
          )}
        </div>

        {/* Actions (likes/comments) */}
        {isPost && (
          <>
            <div className="px-4 py-2 border-t border-zinc-100 flex items-center gap-4">
              <button
                onClick={() => handleLike(item.id)}
                className={`flex items-center gap-1.5 text-sm transition-colors ${
                  isLiked ? 'text-red-500' : 'text-zinc-400 hover:text-red-500'
                }`}
                data-testid={`like-${item.id}`}
              >
                <Heart className={`w-4 h-4 ${isLiked ? 'fill-red-500' : ''}`} />
                <span>{likesCount > 0 ? likesCount : 'Like'}</span>
              </button>
              <button
                onClick={() => toggleComments(item.id)}
                className="flex items-center gap-1.5 text-sm text-zinc-400 hover:text-blue-500 transition-colors"
                data-testid={`toggle-comments-${item.id}`}
              >
                <MessageCircle className="w-4 h-4" />
                <span>{commentCount > 0 ? `${commentCount} comment${commentCount > 1 ? 's' : ''}` : 'Comment'}</span>
                {expandedComments[item.id] ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              </button>
            </div>

            {/* Comments section */}
            {expandedComments[item.id] && (
              <div className="px-4 pb-3 border-t border-zinc-100">
                {loadingComments[item.id] ? (
                  <div className="py-3 flex justify-center">
                    <Loader2 className="w-4 h-4 animate-spin text-zinc-400" />
                  </div>
                ) : (
                  <div className="pt-2 space-y-2">
                    {(comments[item.id] || []).map(c => (
                      <div key={c.id} className="flex gap-2">
                        <div className="w-6 h-6 bg-zinc-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <User className="w-3 h-3 text-zinc-400" />
                        </div>
                        <div className="flex-1 bg-zinc-50 rounded-lg px-3 py-1.5">
                          <div className="flex items-center gap-1.5">
                            <span className="text-xs font-semibold text-zinc-700">{c.user_name}</span>
                            {c.user_role && (
                              <span className={`text-[10px] px-1 py-0 rounded ${getRoleBadge(c.user_role)}`}>{c.user_role}</span>
                            )}
                          </div>
                          <p className="text-xs text-zinc-600">{c.content}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Comment input */}
                <div className="flex gap-2 mt-2">
                  <Input
                    value={commentTexts[item.id] || ''}
                    onChange={(e) => setCommentTexts(prev => ({ ...prev, [item.id]: e.target.value }))}
                    placeholder="Write a comment..."
                    className="flex-1 h-8 text-sm"
                    onKeyDown={(e) => { if (e.key === 'Enter') handleComment(item.id); }}
                    data-testid={`comment-input-${item.id}`}
                  />
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleComment(item.id)}
                    disabled={!commentTexts[item.id]?.trim()}
                    className="text-orange-500 h-8"
                    data-testid={`comment-send-${item.id}`}
                  >
                    <Send className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className={embedded ? '' : 'min-h-screen bg-zinc-50'}>
      {!embedded && (
      <header className="bg-white border-b border-zinc-200 sticky top-0 z-40">
        <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/dashboard')} className="text-zinc-500 hover:text-zinc-700">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center">
              <Users className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-xl tracking-tight text-zinc-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Community</span>
          </div>
          <div className="flex items-center gap-3">
            <ChatButton />
            <NotificationBell />
          </div>
        </div>
      </header>
      )}

      <main className={embedded ? '' : 'container mx-auto px-4 sm:px-6 py-4 sm:py-6 max-w-5xl'}>
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Feed Section */}
          <div className="lg:col-span-2">
            {/* Create Post */}
            <div className="bg-white rounded-xl border border-zinc-200 p-4 mb-4" data-testid="create-post-section">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <User className="w-5 h-5 text-orange-600" />
                </div>
                <div className="flex-1">
                  <Textarea
                    value={newPost}
                    onChange={(e) => setNewPost(e.target.value)}
                    placeholder="What's happening in your community?"
                    className="min-h-[80px] resize-none border-zinc-200 focus:border-orange-300"
                    maxLength={500}
                    data-testid="post-content-input"
                  />
                  {/* Image Preview */}
                  {postImagePreview && (
                    <div className="relative mt-2 inline-block">
                      <img src={postImagePreview} alt="Preview" className="max-h-48 rounded-lg border border-zinc-200 object-cover" />
                      <button
                        onClick={removeImage}
                        className="absolute top-1 right-1 w-6 h-6 bg-zinc-900/70 text-white rounded-full flex items-center justify-center hover:bg-zinc-900"
                        data-testid="remove-image-btn"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  )}
                  <div className="flex items-center justify-between mt-2">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-zinc-400">{newPost.length}/500</span>
                      <label className="cursor-pointer text-zinc-400 hover:text-orange-500 transition-colors" data-testid="image-upload-btn">
                        <ImagePlus className="w-5 h-5" />
                        <input type="file" accept="image/*" className="hidden" onChange={handleImageSelect} />
                      </label>
                      {uploadingImage && <Loader2 className="w-4 h-4 animate-spin text-orange-500" />}
                      {user?.role === 'admin' && (
                        <>
                          <button
                            type="button"
                            onClick={() => setIsAnnouncement(!isAnnouncement)}
                            className={`text-xs px-2 py-1 rounded-full transition-colors ${isAnnouncement ? 'bg-red-100 text-red-700' : 'bg-zinc-100 text-zinc-500 hover:bg-zinc-200'}`}
                            data-testid="toggle-announcement"
                          >
                            Announcement
                          </button>
                          <button
                            type="button"
                            onClick={() => setIsPinned(!isPinned)}
                            className={`transition-colors ${isPinned ? 'text-orange-500' : 'text-zinc-400 hover:text-orange-500'}`}
                            title="Pin to top"
                            data-testid="toggle-pin"
                          >
                            <Pin className="w-4 h-4" />
                          </button>
                        </>
                      )}
                    </div>
                    <Button
                      onClick={handleCreatePost}
                      disabled={(!newPost.trim() && !postImage) || posting}
                      className="bg-orange-500 hover:bg-orange-600 text-white"
                      size="sm"
                      data-testid="submit-post-btn"
                    >
                      {posting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <PenSquare className="w-4 h-4 mr-1" />}
                      Post
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            <Tabs defaultValue="community" className="space-y-4">
              <TabsList className="bg-white border border-zinc-200">
                <TabsTrigger value="community" data-testid="tab-community">Community</TabsTrigger>
                <TabsTrigger value="following" data-testid="tab-following">Following</TabsTrigger>
              </TabsList>

              <TabsContent value="community" className="space-y-3">
                {communityFeed.length === 0 ? (
                  <div className="text-center py-12 bg-white rounded-xl border border-zinc-200">
                    <PenSquare className="w-12 h-12 text-zinc-300 mx-auto mb-3" />
                    <p className="text-zinc-600 font-medium">No posts yet</p>
                    <p className="text-sm text-zinc-400 mt-1">Be the first to share something!</p>
                  </div>
                ) : (
                  communityFeed.map(renderFeedItem)
                )}
              </TabsContent>

              <TabsContent value="following" className="space-y-3">
                {followingFeed.length === 0 ? (
                  <div className="text-center py-12 bg-white rounded-xl border border-zinc-200">
                    <Users className="w-12 h-12 text-zinc-300 mx-auto mb-3" />
                    <p className="text-zinc-600">No activity from people you follow</p>
                    <p className="text-sm text-zinc-400 mt-1">Follow drivers to see their activity</p>
                  </div>
                ) : (
                  followingFeed.map(renderFeedItem)
                )}
              </TabsContent>
            </Tabs>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick Links */}
            <div className="bg-white rounded-xl border border-zinc-200 p-4">
              <h3 className="font-semibold text-zinc-900 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Explore
              </h3>
              <div className="space-y-2">
                <Button
                  variant="outline"
                  className="w-full justify-start text-green-600 border-green-200 hover:bg-green-50"
                  onClick={() => navigate('/people')}
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  Find Friends
                </Button>
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => navigate('/tasks')}
                >
                  <Tag className="w-4 h-4 mr-2" />
                  Task Marketplace
                </Button>
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => navigate('/dashboard')}
                >
                  <Users className="w-4 h-4 mr-2" />
                  Dashboard
                </Button>
              </div>
            </div>

            {/* Following */}
            <div className="bg-white rounded-xl border border-zinc-200 p-4">
              <h3 className="font-semibold text-zinc-900 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Following ({following.length})
              </h3>
              {following.length === 0 ? (
                <p className="text-sm text-zinc-500">You're not following anyone yet</p>
              ) : (
                <div className="space-y-3">
                  {following.slice(0, 5).map((f) => (
                    <div key={f.id} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                          <User className="w-4 h-4 text-orange-600" />
                        </div>
                        <span className="text-sm font-medium text-zinc-900">{f.following_name}</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleUnfollow(f.following_id)}
                        className="text-zinc-400 hover:text-red-500 text-xs"
                      >
                        Unfollow
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Followers */}
            <div className="bg-white rounded-xl border border-zinc-200 p-4">
              <h3 className="font-semibold text-zinc-900 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Followers ({followers.length})
              </h3>
              {followers.length === 0 ? (
                <p className="text-sm text-zinc-500">No followers yet</p>
              ) : (
                <div className="space-y-3">
                  {followers.slice(0, 5).map((f) => (
                    <div key={f.id} className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-zinc-100 rounded-full flex items-center justify-center">
                        <User className="w-4 h-4 text-zinc-600" />
                      </div>
                      <span className="text-sm font-medium text-zinc-900">{f.follower_name}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default CommunityFeed;
